// Where the page runs, the person's Anthropic keys there, and one request of the review sent with
// one of them (brief 48). In a browser the keys are this origin's storage, the shell's own
// (src/core/keys.ts), and the page sends the request itself; in the desktop app the runtime keeps
// the keys in the keychain and sends the request, so this window never holds a key's value.

import { browserKeys, chosenKey, chooseKey, type ProviderKey } from '../../core/keys.ts'
import { Api } from '../../server/api.ts'
import { anthropicKey, CHOICE, redact, withKey, type KeyFound, type Place } from '../core/review.ts'
import type { ReviewRequest, ReviewResponse } from '../core/types.ts'
import { desktop } from './desktop.ts'

/// The device's name to the organisation's server, the one the shell keeps on this origin.
function deviceId(): string {
  let id = localStorage.getItem('interplae.device')
  if (!id) {
    id = `browser-${crypto.randomUUID().slice(0, 8)}`
    localStorage.setItem('interplae.device', id)
  }
  return id
}

/// The organisation's server on this origin, for the session and, in brief 49, the memory.
export const api = (): Api => new Api('', deviceId())

/// Where the page runs. A browser asks its origin's `/api/session`: a member there is an
/// organisation's host with someone signed in; a refusal to sign in is an organisation's host
/// signed out; no server at all, as on align.interplae.com, is neither.
export async function placeOf(): Promise<Place> {
  if (desktop) return { kind: 'desktop' }
  try {
    const mode = await api().mode()
    if (mode.kind === 'member') return { kind: 'member', role: mode.member.role }
    return { kind: 'elsewhere', organisation: mode.kind === 'signed-out' || mode.kind === 'disabled' }
  } catch {
    return { kind: 'elsewhere', organisation: false }
  }
}

async function keys(): Promise<ProviderKey[]> {
  if (desktop) return (await desktop.engine<{ keys: ProviderKey[] }>({ type: 'keys.list' })).keys
  return browserKeys(localStorage, sessionStorage).list()
}

/// The Anthropic key the review uses, as machine translation finds a profile's.
export async function findKey(): Promise<KeyFound> {
  return anthropicKey(await keys(), chosenKey(localStorage, CHOICE))
}

/// One of several keys picked: remembered for the next review.
export const pickKey = (key: ProviderKey) => chooseKey(localStorage, CHOICE, key.id)

/// Settings' Provider Keys: the shell's Machine translation settings at this origin, or in the
/// desktop app the main window's.
export function openKeys(): void {
  if (desktop) void desktop.tell({ kind: 'keys' })
  else window.open('/?settings=machine', '_blank', 'noopener')
}

/// One request sent with the key. Never throws: no answer is a failure the engine records.
export async function send(key: ProviderKey, request: ReviewRequest): Promise<ReviewResponse> {
  if (desktop) {
    try {
      return await desktop.engine<ReviewResponse>({ type: 'align.send', key: key.id, request })
    } catch (e) {
      return { status: 0, headers: [], body: '', failure: { kind: 'unreachable', message: e instanceof Error ? e.message : String(e) } }
    }
  }
  const { credential } = (await browserKeys(localStorage, sessionStorage).handed(key.id)) as { credential: string | null }
  const secret = credential ?? ''
  const { url, init } = withKey(request, secret)
  const controller = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, request.timeout_ms)
  try {
    const response = await fetch(url, { ...init, signal: controller.signal })
    const text = await response.text()
    return { status: response.status, headers: [...response.headers.entries()], body: redact(text, secret), failure: null }
  } catch (e) {
    const message = redact(e instanceof Error ? e.message : String(e), secret)
    return { status: 0, headers: [], body: '', failure: timedOut ? { kind: 'timeout' } : { kind: 'unreachable', message } }
  } finally {
    clearTimeout(timer)
  }
}
